import {CSSProperties, FC, useMemo} from 'react';
import {createGlobalStyle, css, styled} from 'styled-components';

import {cssPx, optional} from '@shared-web/lib/styled_utils';
import {useViewportSize} from '@shared-web/lib/use_screen_size';

interface ViewportWrapperProps {
  widthOverHeightRatio?: {min?: number; max?: number};
  bodyFontSizeRatio?: number; // Font size of the body text will be the width of the body times this ratio
  backgroundColor?: string;
  children: React.ReactNode;
}

// Component that cover the screen and restrict the content to the viewport
// to meet some restrictions (eg: ensure the height is at least twice the width)
export const ViewportWrapper: FC<ViewportWrapperProps> = props => {
  const {widthOverHeightRatio, bodyFontSizeRatio, backgroundColor, children} = props;
  const {width, height} = useViewportSize();

  console.log({width, height});

  const {contentCss, extraStyles} = useMemo<{
    contentCss: CSSProperties;
    extraStyles?: JSX.Element;
  }>(() => {
    const viewportRatio = width / height;

    const s = (w: number): JSX.Element | undefined =>
      bodyFontSizeRatio !== undefined ? <BodyStyle $fontSize={bodyFontSizeRatio * w} /> : undefined;

    if (widthOverHeightRatio === undefined) {
      return {
        contentCss: {top: 0, right: 0, bottom: 0, left: 0},
        extraStyles: s(width),
      };
    }
    const {min, max} = widthOverHeightRatio;
    if (min !== undefined && viewportRatio < min) {
      const targetHeight = width / min;
      const border = (height - targetHeight) / 2;
      return {contentCss: {top: border, right: 0, bottom: border, left: 0}, extraStyles: s(width)};
    } else if (max !== undefined && viewportRatio > max) {
      const targetWidth = max * height;
      const border = (width - targetWidth) / 2;
      return {
        contentCss: {top: 0, right: border, bottom: 0, left: border},
        extraStyles: s(targetWidth),
      };
    }
    return {
      contentCss: {top: 0, right: 0, bottom: 0, left: 0},
      extraStyles: s(width),
    };
  }, [width, height, bodyFontSizeRatio, widthOverHeightRatio]);

  const contentCssWithSafeArea = useMemo<CSSProperties>(() => {
    const {top, right, bottom, left, ...rest} = contentCss;
    function addInset(val: string | number | undefined, pos: string[]): string {
      const inset = pos.map(p => `env(safe-area-inset-${p})`).join(' - ');
      return `calc(${cssPx(val ?? 0)} - ${inset})`;
    }
    return {
      ...rest,
      top,
      right: addInset(right, ['left', 'right']),
      bottom: addInset(bottom, ['top', 'bottom']),
      left,
    };
  }, [contentCss]);

  return (
    <>
      {extraStyles}
      <Wrapper $backgroundColor={backgroundColor} style={contentCssWithSafeArea}>
        {children}
      </Wrapper>
    </>
  );
};

ViewportWrapper.displayName = 'ViewportWrapper';

const Wrapper = styled.div<{$backgroundColor?: string}>`
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  ${p => optional('backgroundColor', p.$backgroundColor)}
`;

export const BodyStyle = createGlobalStyle`${css<{$fontSize: number}>`
  html,
  body {
    font-size: ${p => p.$fontSize}px !important;
  }
`}`;
